import {copyText} from '@github-ui/copy-to-clipboard'
import {AgentIcon, CopyIcon, TerminalIcon, ToolsIcon, VscodeIcon} from '@primer/octicons-react'
import type {ActionMenuButtonProps} from '@primer/react'
import {ActionList, ActionMenu, Tooltip, Truncate} from '@primer/react'
import type {RefObject} from 'react'
import {useCallback, useMemo} from 'react'

import {ClaudeCodeIcon} from './ClaudeCodeIcon'
import {CodexIcon} from './CodexIcon'
import styles from './CodingAssistantButton.module.css'

type CodingAssistantButtonProps = {
  url: string
  buttonLabel: string
  promptBuilder: (issueUrl: string) => string
  size: ActionMenuButtonProps['size']
  showSettingsLink?: boolean
  iconOnly?: boolean
  showIcon?: boolean
  className?: string
  onAssignToAgent?: () => void
  anchorRef?: RefObject<HTMLElement | null>
}

const CODING_TOOLS_SETTINGS_PATH = '/settings/coding_tools'

function buildVSCodeAgentUrl(prompt: string): string {
  const doubleEncoded = encodeURIComponent(encodeURIComponent(prompt))
  return `vscode://github.copilot-chat?mode=agent&prompt=${doubleEncoded}`
}

function buildCodexUrl(prompt: string): string {
  const encoded = encodeURIComponent(prompt)
  return `codex://new?prompt=${encoded}`
}

function buildClaudeCodeUrl(prompt: string): string {
  const encoded = encodeURIComponent(prompt)
  return `https://claude.ai/code?prompt=${encoded}`
}

const assistants = [
  {icon: VscodeIcon, label: 'Copilot for VS Code', urlBuilder: buildVSCodeAgentUrl},
  {icon: CodexIcon, label: 'Codex Desktop', urlBuilder: buildCodexUrl},
  {icon: ClaudeCodeIcon, label: 'Claude Code', urlBuilder: buildClaudeCodeUrl},
]

export function CodingAssistantButton({
  url,
  buttonLabel,
  promptBuilder,
  size,
  showSettingsLink = false,
  iconOnly = false,
  showIcon = true,
  className,
  onAssignToAgent,
  anchorRef,
}: CodingAssistantButtonProps) {
  const prompt = useMemo(() => promptBuilder(url), [promptBuilder, url])

  const handleCopyAsPrompt = useCallback(() => {
    void copyText(prompt)
  }, [prompt])

  return (
    <ActionMenu anchorRef={anchorRef}>
      {iconOnly ? (
        <Tooltip text={buttonLabel} type="label">
          <ActionMenu.Button className={className} leadingVisual={TerminalIcon} size={size} />
        </Tooltip>
      ) : (
        <ActionMenu.Button
          aria-label={buttonLabel}
          className={className}
          leadingVisual={showIcon ? TerminalIcon : undefined}
          size={size}
        >
          {buttonLabel}
        </ActionMenu.Button>
      )}
      <ActionMenu.Overlay className={styles.overlay}>
        <ActionList>
          <ActionList.Item onSelect={handleCopyAsPrompt}>
            <ActionList.LeadingVisual>
              <TerminalIcon />
            </ActionList.LeadingVisual>
            Copy as prompt
            <ActionList.Description variant="block">
              <Truncate maxWidth={240} title={prompt}>
                {prompt}
              </Truncate>
            </ActionList.Description>
            <ActionList.TrailingVisual>
              <CopyIcon />
            </ActionList.TrailingVisual>
          </ActionList.Item>
          {onAssignToAgent && (
            <>
              <ActionList.Divider className={styles.divider} />
              <ActionList.Item onSelect={onAssignToAgent}>
                <ActionList.LeadingVisual>
                  <AgentIcon />
                </ActionList.LeadingVisual>
                Cloud agent
              </ActionList.Item>
            </>
          )}
          <ActionList.Divider className={styles.divider} />
          <ActionList.Group>
            <ActionList.GroupHeading>Open in</ActionList.GroupHeading>
            {assistants.map(({icon: Icon, label, urlBuilder}) => (
              <ActionList.Item key={label} onSelect={() => window.open(urlBuilder(prompt), '_self')}>
                <ActionList.LeadingVisual>
                  <Icon />
                </ActionList.LeadingVisual>
                {label}
              </ActionList.Item>
            ))}
          </ActionList.Group>
          {showSettingsLink && (
            <>
              <ActionList.Divider className={styles.divider} />
              <ActionList.LinkItem href={CODING_TOOLS_SETTINGS_PATH}>
                <ActionList.LeadingVisual>
                  <ToolsIcon />
                </ActionList.LeadingVisual>
                Configure coding tools
              </ActionList.LinkItem>
            </>
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
